<template>
  <v-container>
    <h1>Взаиморасчеты между филиалами</h1>
    <!-- Блок "Назад к списку отчетов" -->
    <div class="mb-5">
      <router-link to="/reports">
        Назад к списку отчётов
      </router-link>
    </div>

    <my-loader :loader="loader"></my-loader>

    <!-- Блок "Ранее сформированные отчеты" -->
    <v-card
      v-if="reportList.length"
      class="mb-4"
    >
      <v-card-title>Ранее сформированные отчёты</v-card-title>
      <v-card-text>
        <v-progress-circular
          v-if="loader"
          indeterminate
          color="primary"
          size="40"
          class="my-4"
        />
        <v-data-table
          v-if="!loader"
          :headers="pastReportHeaders"
          :items="reportList"
          hide-default-footer
          :items-per-page="-1"
        >
          <template v-slot:item.created="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>

          <template v-slot:item.period="{ item }">
            {{ formatDate(item.date_from) }} — {{ formatDate(item.date_to) }}
          </template>

          <template v-slot:item.status="{ item }">
            <span class="d-flex align-center">
              {{ statusLabel(item.report_status) }}
              <v-progress-circular
                v-if="item.report_status === 'PROCESSING'"
                indeterminate
                color="primary"
                size="16"
                width="2"
                class="ml-2"
              />
            </span>
          </template>

          <template v-slot:item.action="{ item }">
            <a
              v-if="item.report_status != 'PROCESSING'"
              :href="'https://lk.xbadm.ru' + item.url"
            >
              Скачать
            </a>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Блок "Запросить новый отчет" -->
    <v-card>
      <v-card-title>Запросить новый отчет</v-card-title>
      <v-card-text>
        <!-- Форма запроса отчета -->
        <v-row>
          <v-col
            cols="12"
            sm="6"
          >
            <v-menu
              v-model="menuFrom"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateFrom"
                  label="Дата начала"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dateFrom"
                @input="menuFrom = false"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col
            cols="12"
            sm="6"
          >
            <v-menu
              v-model="menuTo"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateTo"
                  label="Дата окончания"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dateTo"
                @input="menuTo = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>

        <v-btn
          color="primary"
          :disabled="!dateFrom || !dateTo"
          @click="createSettlementsReport"
        >
          Сформировать отчет
        </v-btn>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import Api from '@/api/api'
import MyLoader from '@/components/MyLoader.vue'

export default {
  name: 'BranchPayments',
  components: {
    MyLoader,
  },

  data() {
    return {
      loader: false,
      reportList: [],
      dateFrom: null,
      dateTo: null,
      menuFrom: false,
      menuTo: false,
      reportPollingInterval: null,
      pastReportHeaders: [
        { text: 'Создан', value: 'created' },
        { text: 'Период', value: 'period' },
        { text: 'Статус', value: 'status' },
        { text: 'Действие', value: 'action', sortable: false },
      ],

    }
  },

  beforeDestroy() {
    if (this.reportPollingInterval) {
      clearInterval(this.reportPollingInterval)
    }
  },

  async mounted() {
    await this.getSettlementsReports()
  },

  methods: {
    async getSettlementsReports() {
      this.loader = true
      try {
        this.reportList = await Api.getSettlementsReports()
        this.setupReportPolling()
      } catch (error) {
        console.error('Ошибка при загрузке отчетов:', error)
      } finally {
        this.loader = false
      }
    },

    async createSettlementsReport() {
      this.loader = true
      try {
        await Api.createSettlementsReport({
          date_from: this.dateFrom,
          date_to: this.dateTo,
        })
        await this.getSettlementsReports()
      } catch (error) {
        this.handleReportError(error)
      } finally {
        this.loader = false
      }
    },

    setupReportPolling() {
      const hasProcessing = this.reportList.some(r => r.report_status === 'PROCESSING')
      if (hasProcessing && !this.reportPollingInterval) {
        this.reportPollingInterval = setInterval(() => {
          this.getSettlementsReports()
        }, 60000)
      } else if (!hasProcessing && this.reportPollingInterval) {
        clearInterval(this.reportPollingInterval)
        this.reportPollingInterval = null
      }
    },

    handleReportError(error) {
      console.error(error)
      if (error.response?.status === 409) {
        alert('Уже есть незавершенный процесс формирования отчета. Пожалуйста, подождите.')
      } else {
        alert('Произошла ошибка при формировании отчета')
      }
    },

    statusLabel(status) {
      switch (status) {
        case 'PROCESSING':
          return 'Формируется'
        case 'READY':
          return 'Готов'
        case 'ERROR':
          return 'Ошибка'
        default:
          return status
      }
    },

    formatDateTime(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    },
  },
}
</script>
